import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import { opcoesDeCatalogo } from "./useOpcoesInventario";
import { SeletorAtivo } from "./AtivoFormModal";
import SeletorResponsavel from "./SeletorResponsavel";

const INPUT = "border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-full";

/**
 * Operações de movimentação de um ativo. Todas as regras (transições, motivos obrigatórios,
 * responsável exigido, vínculos) são validadas no backend; aqui só se coleta o necessário
 * para cada tipo de operação.
 */
export default function MovimentarAtivoModal({ visible, onHide, service, ativo, opcoes, onConcluido }) {
    const { metadados, catalogos = {}, filiais = [] } = opcoes;

    const [tipo, setTipo] = useState(null);
    const [form, setForm] = useState({});
    const [principalDescricao, setPrincipalDescricao] = useState("");
    const [confirmou, setConfirmou] = useState(false);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (visible) {
            setTipo(null);
            setForm({});
            setConfirmou(false);
            setPrincipalDescricao("");
        }
    }, [visible]);

    if (!ativo) return null;

    const operacoes = (metadados?.operacoes || []).filter(op => {
        if (!ativo.encerrado) return true;
        return op.tipo === "DESCARTE" && ativo.fase === "BAIXADO";
    });
    const operacao = operacoes.find(o => o.tipo === tipo);
    const definir = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const opcoesStatus = opcoesDeCatalogo(catalogos.STATUS);

    const campos = {
        TRANSFERENCIA_FILIAL: ["filialId", "departamentoId", "localizacao"],
        MUDANCA_DEPARTAMENTO: ["departamentoId"],
        TROCA_RESPONSAVEL: ["responsavel"],
        ALTERACAO_LOCALIZACAO: ["localizacao"],
        VINCULO_EQUIPAMENTO: ["equipamentoPrincipalId"],
        ALTERACAO_STATUS: ["statusId", "responsavel"],
        EMPRESTIMO: ["responsavel"],
        DEVOLUCAO: [],
        MANUTENCAO: [],
        BAIXA: [],
        DESCARTE: [],
    }[tipo] || [];

    const confirmar = async () => {
        if (!tipo) {
            toast.error("Selecione a operação.");
            return;
        }
        if (operacao?.exigeMotivo && !(form.motivo || "").trim()) {
            toast.error("Informe o motivo da operação.");
            return;
        }
        if (operacao?.confirmacaoCritica && !confirmou) {
            toast.error("Confirme que deseja realizar esta operação.");
            return;
        }
        setSalvando(true);
        try {
            const res = await service.movimentar(ativo.id, { tipo, ...form });
            toast.success("Movimentação registrada com sucesso!");
            onConcluido?.(res.data);
            onHide();
        } catch (e) {
            // mensagem do backend exibida pelo interceptor
        } finally {
            setSalvando(false);
        }
    };

    const rotulo = (texto, children) => (
        <div className="mb-3">
            <label className="block text-xs text-muted-foreground mb-1">{texto}</label>
            {children}
        </div>
    );

    return (
        <Dialog
            header={`Movimentar ativo — ${ativo.nomenclatura || ativo.patrimonio || `#${ativo.id}`}`}
            visible={visible}
            onHide={onHide}
            blockScroll
            className="w-[96%] md:w-[560px]"
            contentClassName="pt-3"
        >
            <p className="text-xs text-muted-foreground mb-3">
                Situação atual: <strong>{ativo.statusNome}</strong> · Filial: <strong>{ativo.filialNome || "—"}</strong>
            </p>

            {operacoes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    Este ativo está {ativo.faseRotulo?.toLowerCase()} e não aceita novas movimentações.
                </p>
            ) : (
                <>
                    {rotulo("Operação *",
                        <Dropdown
                            className="w-full text-sm"
                            value={tipo}
                            options={operacoes.map(o => ({ value: o.tipo, label: o.rotulo }))}
                            onChange={e => { setTipo(e.value); setForm({}); setConfirmou(false); }}
                            placeholder="Selecione a operação"
                        />)}

                    {campos.includes("filialId") && rotulo("Filial de destino *",
                        <Dropdown className="w-full text-sm" filter value={form.filialId ?? null}
                            options={filiais.map(f => ({ value: f.id, label: f.nome }))}
                            onChange={e => definir("filialId", e.value)} placeholder="Selecione..." />)}

                    {campos.includes("departamentoId") && rotulo(
                        tipo === "MUDANCA_DEPARTAMENTO" ? "Novo departamento *" : "Novo departamento (opcional)",
                        <Dropdown className="w-full text-sm" filter showClear value={form.departamentoId ?? null}
                            options={opcoesDeCatalogo(catalogos.DEPARTAMENTO)}
                            onChange={e => definir("departamentoId", e.value)} placeholder="Selecione..." />)}

                    {campos.includes("localizacao") && rotulo(
                        tipo === "ALTERACAO_LOCALIZACAO" ? "Nova localização" : "Nova localização (opcional)",
                        <input className={INPUT} value={form.localizacao ?? ""} onChange={e => definir("localizacao", e.target.value)} />)}

                    {campos.includes("responsavel") && (
                        <>
                            {rotulo(
                                tipo === "EMPRESTIMO" ? "Quem receberá o equipamento *"
                                    : tipo === "ALTERACAO_STATUS" ? "Responsável (opcional)"
                                        : "Novo responsável (vazio remove o responsável)",
                                <SeletorResponsavel
                                    service={service}
                                    nome={form.responsavelNome}
                                    cargo={form.responsavelCargo}
                                    onChange={({ nome, cargo }) => setForm(prev => ({ ...prev, responsavelNome: nome, responsavelCargo: cargo }))}
                                />)}
                            {rotulo("Cargo do responsável (opcional)",
                                <input className={INPUT} maxLength={150} value={form.responsavelCargo ?? ""}
                                    onChange={e => definir("responsavelCargo", e.target.value)} />)}
                        </>
                    )}

                    {campos.includes("statusId") && rotulo("Novo status *",
                        <Dropdown className="w-full text-sm" filter value={form.statusId ?? null}
                            options={opcoesStatus}
                            onChange={e => definir("statusId", e.value)} placeholder="Selecione..." />)}

                    {campos.includes("equipamentoPrincipalId") && rotulo(
                        "Equipamento principal (deixe vazio para desvincular)",
                        <SeletorAtivo service={service} valorId={form.equipamentoPrincipalId}
                            descricao={principalDescricao} excluirId={ativo.id}
                            onChange={(id, desc) => { definir("equipamentoPrincipalId", id); setPrincipalDescricao(desc); }} />)}

                    {tipo && rotulo(operacao?.exigeMotivo ? "Motivo *" : "Motivo / observação (opcional)",
                        <textarea rows={3} className={INPUT} value={form.motivo ?? ""}
                            onChange={e => definir("motivo", e.target.value)} />)}

                    {operacao?.confirmacaoCritica && (
                        <label className="flex items-start gap-2 text-sm bg-destructive/10 border border-destructive/30 rounded-md p-3 mb-3">
                            <input type="checkbox" className="mt-1" checked={confirmou} onChange={e => setConfirmou(e.target.checked)} />
                            <span>
                                Confirmo a operação de <strong>{operacao.rotulo.toLowerCase()}</strong>. O histórico e os vínculos
                                do ativo serão preservados, mas ele deixará de aceitar edições e novas movimentações.
                            </span>
                        </label>
                    )}
                </>
            )}

            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-border">
                <Button label="Cancelar" variant="gray" onClick={onHide} />
                {operacoes.length > 0 && (
                    <Button
                        label="Confirmar"
                        variant={operacao?.confirmacaoCritica ? "red" : "indigo"}
                        loading={salvando}
                        disabled={!tipo}
                        onClick={confirmar}
                    />
                )}
            </div>
        </Dialog>
    );
}
